import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Send, RotateCcw, ChevronDown } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { api } from '@/lib/api';

interface Msg { role: 'user' | 'assistant'; content: string; }

// Quick suggestions per page context
const PAGE_SUGGESTIONS: { match: string; role?: string; qs: string[] }[] = [
  { match: '/dashboard',   role: 'CLIENT',  qs: ['Comment lire mon tableau de bord ?', 'Comment créer une nouvelle mission ?'] },
  { match: '/dashboard',   role: 'JURISTE', qs: ['Comment gérer mes missions en attente ?', 'Comment saisir mon temps de travail ?'] },
  { match: '/dashboard',   role: 'ADMIN',   qs: ['Comment lire les KPIs ?', 'Comment gérer une alerte SLA ?'] },
  { match: '/missions',    role: 'CLIENT',  qs: ['Comment créer une mission ?', 'Comment envoyer un document à mon juriste ?', 'Comment suivre l\'avancement ?'] },
  { match: '/missions',    role: 'JURISTE', qs: ['Comment changer le statut d\'une mission ?', 'Comment enregistrer du temps ?'] },
  { match: '/templates',   role: 'CLIENT',  qs: ['Comment remplir un formulaire ?', 'Comment sauvegarder un brouillon ?', 'Quand puis-je télécharger le document ?'] },
  { match: '/templates',   role: 'JURISTE', qs: ['Comment insérer une variable typée ?', 'Comment créer un bloc conditionnel ?', 'Comment partager avec un client ?', 'Comment valider une soumission ?'] },
  { match: '/lexbase',     qs: ['Comment rechercher un document ?', 'Comment soumettre un document ?', 'Comment filtrer par juridiction ?'] },
  { match: '/vaegbase',    qs: ['Comment rechercher un document ?', 'Comment soumettre un document ?'] },
  { match: '/lexwork',     qs: ['Comment utiliser une procédure guidée ?', 'Comment partir d\'un document VaegBase ?'] },
  { match: '/messages',    qs: ['Comment envoyer une pièce jointe ?', 'Comment retrouver un ancien message ?'] },
  { match: '/signatures',  qs: ['Comment initier une signature ?', 'Comment suivre le statut ?'] },
  { match: '/subscription',qs: ['Comment changer de plan ?', 'Comment télécharger une facture ?'] },
  { match: '/clients',     qs: ['Comment voir les missions d\'un client ?', 'Comment créer un compte client ?'] },
  { match: '/juristes',    qs: ['Comment voir les statistiques d\'un juriste ?', 'Comment réassigner une mission ?'] },
  { match: '/users',       qs: ['Comment créer un nouveau compte ?', 'Comment désactiver un utilisateur ?'] },
  { match: '/invoices',    qs: ['Comment créer une facture ?', 'Comment enregistrer un paiement ?'] },
  { match: '/audit',       qs: ['Comment filtrer les actions ?', 'Comment exporter le journal ?'] },
];

function getSuggestions(path: string, role: string): string[] {
  const matches = PAGE_SUGGESTIONS.filter(s =>
    path.includes(s.match) && (!s.role || s.role === role)
  );
  if (matches.length === 0) {
    if (role === 'CLIENT') return ['Comment utiliser la plateforme ?', 'Comment contacter mon juriste ?', 'Où trouver mes documents ?'];
    if (role === 'JURISTE') return ['Comment utiliser VaegCreate ?', 'Comment accéder à VaegBase ?', 'Comment valider une soumission ?'];
    return ['Comment gérer les utilisateurs ?', 'Comment lire les KPIs ?', 'Comment accéder au journal d\'audit ?'];
  }
  return matches.flatMap(m => m.qs).slice(0, 4);
}

function roleLabel(role: string): string {
  if (role === 'CLIENT') return 'client';
  if (role === 'JURISTE') return 'juriste';
  return 'administrateur';
}

function formatContent(text: string): React.ReactNode[] {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    if (line.startsWith('• ') || line.startsWith('- ')) {
      return <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}><span style={{ flexShrink: 0, marginTop: 1 }}>•</span><span>{line.slice(2)}</span></div>;
    }
    if (/^\d+\./.test(line)) {
      return <div key={i} style={{ marginBottom: 3 }}>{line}</div>;
    }
    if (line.trim() === '') return <div key={i} style={{ height: 6 }} />;
    return <span key={i}>{line}{i < lines.length - 1 ? '\n' : ''}</span>;
  });
}

export default function VaegGuide() {
  const { user } = useAuthStore();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const role = user?.role ?? 'CLIENT';
  const suggestions = getSuggestions(location.pathname, role);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [open, msgs]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;
    const userMsg: Msg = { role: 'user', content: text.trim() };
    const next = [...msgs, userMsg];
    setMsgs(next);
    setInput('');
    setShowSuggestions(false);
    setLoading(true);
    try {
      const { data } = await api.post('/guide/chat', {
        messages: next,
        page: location.pathname,
      });
      setMsgs(m => [...m, { role: 'assistant', content: data.data.reply }]);
    } catch {
      setMsgs(m => [...m, { role: 'assistant', content: 'Désolé, je suis temporairement indisponible. Réessayez dans un instant.' }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setMsgs([]); setInput(''); setShowSuggestions(true); };

  return (
    <>
      {/* Floating button */}
      <button
        className="workspace-guide-launcher"
        onClick={() => setOpen(p => !p)}
        aria-label="VaegGuide — Aide plateforme"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9000,
          width: 56, height: 56, borderRadius: '50%',
          background: open ? '#1A1710' : 'linear-gradient(135deg,#D4B866,#8A7008)',
          border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s', color: '#fff',
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {open
          ? <ChevronDown size={22} />
          : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="3" fill="white" />
              <circle cx="12" cy="4" r="1.5" fill="white" opacity="0.6" />
              <circle cx="12" cy="20" r="1.5" fill="white" opacity="0.6" />
              <circle cx="4" cy="12" r="1.5" fill="white" opacity="0.6" />
              <circle cx="20" cy="12" r="1.5" fill="white" opacity="0.6" />
              <circle cx="6.3" cy="6.3" r="1.2" fill="white" opacity="0.4" />
              <circle cx="17.7" cy="17.7" r="1.2" fill="white" opacity="0.4" />
              <circle cx="6.3" cy="17.7" r="1.2" fill="white" opacity="0.4" />
              <circle cx="17.7" cy="6.3" r="1.2" fill="white" opacity="0.4" />
            </svg>
          )
        }
        <span>{open ? 'Fermer l’aide' : 'Besoin d’aide ?'}</span>
      </button>

      {/* Chat panel */}
      {open && (
        <div className="workspace-guide-panel" style={{
          position: 'fixed', bottom: 92, right: 24, zIndex: 8998,
          width: 380, maxHeight: '72vh',
          background: '#fff', borderRadius: 18,
          boxShadow: '0 16px 60px rgba(0,0,0,0.2), 0 4px 20px rgba(0,0,0,0.1)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          animation: 'vg-slide-up 0.2s ease-out',
        }}>

          {/* Header */}
          <div style={{ padding: '14px 16px', background: 'linear-gradient(135deg,#1A1710,#2A2510)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#D4B866,#8A7008)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="3" fill="white" />
                <circle cx="12" cy="4" r="1.5" fill="white" opacity="0.7" />
                <circle cx="12" cy="20" r="1.5" fill="white" opacity="0.7" />
                <circle cx="4" cy="12" r="1.5" fill="white" opacity="0.7" />
                <circle cx="20" cy="12" r="1.5" fill="white" opacity="0.7" />
                <circle cx="6.3" cy="6.3" r="1.2" fill="white" opacity="0.4" />
                <circle cx="17.7" cy="17.7" r="1.2" fill="white" opacity="0.4" />
                <circle cx="6.3" cy="17.7" r="1.2" fill="white" opacity="0.4" />
                <circle cx="17.7" cy="6.3" r="1.2" fill="white" opacity="0.4" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#E8D8A8' }}>VaegGuide</div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>Assistant plateforme · espace {roleLabel(role)}</div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {msgs.length > 0 && (
                <button onClick={reset} title="Nouvelle conversation"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#E8D8A8')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}>
                  <RotateCcw size={14} />
                </button>
              )}
              <button onClick={() => setOpen(false)} title="Fermer"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#E8D8A8')}
                onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}>
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>

            {/* Welcome message */}
            {msgs.length === 0 && (
              <div style={{ background: '#FEF9EC', border: '1px solid #E8D8A8', borderRadius: 12, padding: '12px 14px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1710', marginBottom: 4 }}>Bonjour ! Je suis VaegGuide 👋</div>
                <div style={{ fontSize: 12, color: '#78716C', lineHeight: 1.6 }}>
                  Je suis votre assistant dédié pour maîtriser la plateforme VaegAssist.
                  Posez-moi n'importe quelle question sur les fonctionnalités disponibles dans votre espace {roleLabel(role)}.
                </div>
              </div>
            )}

            {/* Conversation messages */}
            {msgs.map((m, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                {m.role === 'assistant' && (
                  <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 3, marginLeft: 2 }}>VaegGuide</div>
                )}
                <div style={{
                  maxWidth: '88%',
                  background: m.role === 'user' ? 'linear-gradient(135deg,#D4B866,#8A7008)' : '#F8F7F4',
                  color: m.role === 'user' ? '#fff' : '#1A1710',
                  padding: '9px 12px', fontSize: 12, lineHeight: 1.65,
                  borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  whiteSpace: 'pre-wrap',
                }}>
                  {m.role === 'assistant' ? formatContent(m.content) : m.content}
                </div>
              </div>
            ))}

            {/* Loading dots */}
            {loading && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <div style={{ background: '#F8F7F4', borderRadius: '14px 14px 14px 4px', padding: '10px 14px', display: 'flex', gap: 5, alignItems: 'center' }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#B8960A', animation: `vg-dot 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Quick suggestions */}
          {showSuggestions && suggestions.length > 0 && (
            <div style={{ padding: '8px 14px', borderTop: '1px solid #F0EBD8', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <div style={{ width: '100%', fontSize: 10, color: '#9ca3af', fontWeight: 600, letterSpacing: '0.05em', marginBottom: 2 }}>SUGGESTIONS</div>
              {suggestions.map(q => (
                <button key={q} onClick={() => sendMessage(q)}
                  style={{ fontSize: 11, padding: '5px 10px', background: '#FEF9EC', border: '1px solid #E8D8A8', borderRadius: 20, cursor: 'pointer', color: '#78716C', transition: 'all 0.12s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#1A1710'; e.currentTarget.style.color = '#E8D8A8'; e.currentTarget.style.borderColor = '#1A1710'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#FEF9EC'; e.currentTarget.style.color = '#78716C'; e.currentTarget.style.borderColor = '#E8D8A8'; }}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: '10px 14px', borderTop: '1px solid #F0EBD8', display: 'flex', gap: 8 }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
              placeholder="Posez votre question…"
              disabled={loading}
              style={{ flex: 1, padding: '9px 12px', border: '1.5px solid #E8D8A8', borderRadius: 10, fontSize: 12, outline: 'none', background: '#fff', transition: 'border-color 0.15s' }}
              onFocus={e => (e.target.style.borderColor = '#B8960A')}
              onBlur={e => (e.target.style.borderColor = '#E8D8A8')}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
              style={{ width: 36, height: 36, borderRadius: 10, border: 'none', cursor: input.trim() && !loading ? 'pointer' : 'not-allowed', background: input.trim() && !loading ? 'linear-gradient(135deg,#D4B866,#8A7008)' : '#E5E7EB', color: input.trim() && !loading ? '#fff' : '#9CA3AF', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}>
              <Send size={14} />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes vg-slide-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes vg-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
        @keyframes vg-dot { 0%,80%,100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }
      `}</style>
    </>
  );
}
